import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpEvent, HttpEventType, HttpHeaders, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import {
    BehaviorSubject,
    filter,
    Observable,
    scan,
    tap,
} from 'rxjs';
import { Upload } from '../shared/ui-upload/ui-upload.model';
import { Store } from '@ngrx/store';
import { BYPASS_AUTHENTICATION_TOKEN } from './authentication-interceptor.service'
import { sum } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class UploadService {
    constructor(public http: HttpClient, public store: Store) {
    }

    private uploadProgressSubject = new BehaviorSubject<Upload>({ state: 'PENDING', progress: 0, fileProgress: {} });
    public uploadProgress$: Observable<Upload> = this.uploadProgressSubject.asObservable();

    public getProgress(): Upload {
        return this.uploadProgressSubject.value;
    }

    public setProgress(upload: Upload): void {
        this.uploadProgressSubject.next(upload);
    }

    public clearProgress(): void {
        this.uploadProgressSubject.next({ state: 'PENDING', progress: 0, fileProgress: {} });
    }

    public uploadPresignedUrl<T extends { url: string, file: Blob, filename?: string, headers?: Record<string, string> }>(request: T): Observable<Upload> {
        const data = new FormData();
        data.append('file', request.file);
        const initialState: Upload = { state: 'PENDING', progress: 0, fileProgress: {} };
        const calculateState = (upload: Upload, event: HttpEvent<unknown>): Upload => {
            if (this.isHttpProgressEvent(event)) {
                const current = this.getProgress();
                const singleProgress = event.total ? Math.round((100 * event.loaded) / event.total) : current.progress;
                const fileProgress = current.fileProgress!;
                fileProgress[request?.filename ?? request?.url] = singleProgress;
                const globalProgress = sum(Object.values(fileProgress)) / (Object.values(fileProgress).length * 100);
                return {
                    progress: globalProgress,
                    state: 'IN_PROGRESS',
                    fileProgress,
                };
            }
            if (this.isHttpResponse(event)) {
                const current = this.getProgress();
                const singleProgress = 100;
                const fileProgress = current.fileProgress!;
                fileProgress[request?.filename ?? request?.url] = singleProgress;
                const globalProgress = Object.values(fileProgress)
                    .reduce((acc, curr) => acc + curr, 0) / Object.values(fileProgress).length;

                return {
                    progress: globalProgress,
                    state: 'DONE',
                    fileProgress,
                };
            }
            return upload;
        };

        return this.http
            .put(request.url, request.file, {
                // headers: new HttpHeaders({
                //   'Access-Control-Allow-Origin': '*',
                // }),
                headers: new HttpHeaders({
                    ...(request.headers ?? {}),
                    'Content-Type': request.file.type, // Add content type header
                    'Access-Control-Allow-Origin': '*',
                    'ngsw-bypass': 'true', // This is required to get the progress of file uploading, because service worker blocks the progress events
                }),
                context: new HttpContext().set(BYPASS_AUTHENTICATION_TOKEN, true),
                reportProgress: true,
                observe: 'events',
            })
            .pipe(
                scan(calculateState, initialState),
                tap((state: Upload) => {
                    this.setProgress(state);
                }),
                filter(val => val.state === 'DONE'),
            );
    }

    private isHttpProgressEvent(event: HttpEvent<unknown>): event is HttpProgressEvent {
        return event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.UploadProgress;
    }

    private isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
        return event.type === HttpEventType.Response;
    }
}